import { WARNING_TYPE_SPELLING } from 'common/constants/constants'

export interface ContentWarning {
    type: string 
    match: string 
    offset: number 
    length: number 
}

export interface ValidationErrorItem {
    title: string
}

export interface ValidationApiResponse {
    errors?: ValidationErrorItem[]
    error?: string
    meta?: {
        content_warnings?: ContentWarning[]
    }
}

const warnings = (r: ValidationApiResponse): ContentWarning[] => r.meta?.content_warnings ?? []

export const isHardBlockResponse = (r: ValidationApiResponse): boolean =>
    (r.errors?.length ?? 0) > 0 ||
    !!r.error ||
    warnings(r).some((w) => w.type !== WARNING_TYPE_SPELLING)

export const getHardBlockTitles = (r: ValidationApiResponse): string[] => {
    if (r.error) return [r.error]
    return r.errors?.map((e) => e.title) ?? []
}

/**
 * Maps known API-level r.error strings to their frontend i18n keys.
 * r.error is the authoritative rejection reason — it takes priority over
 * content_warning types when determining the banner message.
 */
const ERROR_MESSAGE_TO_I18N_KEY: Readonly<Record<string, string>> = {
    "This doesn't look like a style prompt — try describing how you want minutes written.":
        'STYLE_PROMPT_GUARDRAIL_ERROR',
    'Prompt contains disallowed HTML or script content.': 'STYLE_PROMPT_HTML_CONTENT_ERROR'
}

/**
 * Maps a content-warning type to its frontend i18n key.
 * Used as a secondary signal when r.error is absent.
 * Types sharing the same UX message map to the same key.
 * Unlisted types fall back to the generic disallowed-content key.
 */
const HARD_BLOCK_TYPE_I18N_KEY: Readonly<Record<string, string>> = {
    guardrail: 'STYLE_PROMPT_GUARDRAIL_ERROR',
    html_tag: 'STYLE_PROMPT_HTML_CONTENT_ERROR',
    xss: 'STYLE_PROMPT_HTML_CONTENT_ERROR',
    sql_injection: 'STYLE_PROMPT_HTML_CONTENT_ERROR'
}

const HARD_BLOCK_FALLBACK_I18N_KEY = 'STYLE_PROMPT_DISALLOWED_CONTENT_ERROR'

/**
 * Returns i18n keys for the hard-block error banner.
 *
 * Priority:
 *  1. r.error is present → map to known i18n key, or generic fallback.
 *     r.error is the canonical rejection reason regardless of what
 *     content_warning types are also present.
 *  2. No r.error but non-spelling content_warnings exist → derive keys
 *     from warning types (deduped).
 *  3. r.errors[] present → generic fallback.
 *  4. Nothing to display → [].
 */
export const getHardBlockI18nKeys = (r: ValidationApiResponse): string[] => {
    if (r.error) {
        return [ERROR_MESSAGE_TO_I18N_KEY[r.error] ?? HARD_BLOCK_FALLBACK_I18N_KEY]
    }
    const hardWarnings = getHardBlockWarnings(r)
    if (hardWarnings.length > 0) {
        const seen = new Set<string>()
        const keys: string[] = []
        for (const w of hardWarnings) {
            const key = HARD_BLOCK_TYPE_I18N_KEY[w.type] ?? HARD_BLOCK_FALLBACK_I18N_KEY
            if (!seen.has(key)) {
                seen.add(key)
                keys.push(key)
            }
        }
        return keys
    }
    if ((r.errors?.length ?? 0) > 0) {
        return [HARD_BLOCK_FALLBACK_I18N_KEY]
    }
    return []
}

/**
 * Strips content warnings whose offset/length fields are invalid or out-of-bounds
 * for the given text. Must be called before storing API warnings into component state
 * to prevent crafted offsets from highlighting arbitrary text regions.
 */
export const sanitizeContentWarnings = (
    raw: ContentWarning[],
    textLength: number
): ContentWarning[] =>
    raw.filter((w) => w.offset >= 0 && w.length > 0 && w.offset + w.length <= textLength)

/** Spelling warnings for the soft-block confirmation dialog. */
export const getSpellingWarnings = (r: ValidationApiResponse): ContentWarning[] =>
    warnings(r).filter((w) => w.type === WARNING_TYPE_SPELLING)

/** Non-spelling content warnings for the hard-block inline display (e.g. html_tag, sql_injection). */
export const getHardBlockWarnings = (r: ValidationApiResponse): ContentWarning[] =>
    warnings(r).filter((w) => w.type !== WARNING_TYPE_SPELLING)

/**
 * Adjusts highlight offsets after a text edit so that remaining highlights
 * continue to point at the correct positions in the updated text.
 *
 * Algorithm (Option B — offset tracking):
 *  1. Diff oldText vs newText to find the single contiguous change region:
 *       start            — first character that differs
 *       start+deleted    — end of the deleted span in oldText
 *       start+inserted   — end of the inserted span in newText
 *  2. Remove any highlight whose range overlaps the edited region (user
 *     touched that exact issue).
 *  3. Shift any highlight that comes entirely after the edit by
 *     (inserted - deleted) characters.
 *  4. Highlights before the edit are left untouched.
 */
export const adjustHighlightsForEdit = (
    highlights: ContentWarning[],
    oldText: string,
    newText: string
): ContentWarning[] => {
    if (!highlights.length) return highlights

    let start = 0
    while (start < oldText.length && start < newText.length && oldText[start] === newText[start]) {
        start++
    }

    let oldEnd = oldText.length
    let newEnd = newText.length
    while (oldEnd > start && newEnd > start && oldText[oldEnd - 1] === newText[newEnd - 1]) {
        oldEnd--
        newEnd--
    }

    const deletedEnd = oldEnd // exclusive end of deleted region in oldText
    const delta = newEnd - start - (oldEnd - start) // +insert / -delete

    return highlights
        .filter((h) => {
            const hEnd = h.offset + h.length
            return !(h.offset < deletedEnd && hEnd > start)
        })
        .map((h) =>
            h.offset >= deletedEnd ? { ...h, offset: h.offset + delta } : h
        )
}
